import _ from 'underscore'
import React from 'react'
import styled from 'styled-components'

import Checkbox from 'components/Checkbox'
import Tooltip from 'components/Tooltip'

import { color } from 'styles/colors'

import { DeviceMaintenanceTrendGroup, DeviceMetric } from 'types/DeviceMetric'

type Props = {
  metricAndGroupListItem: DeviceMaintenanceTrendGroup | DeviceMetric
  showMetricSelection: boolean,
  selectedMetrics: string[],
  hideChart: boolean,
  handleMetricSelection: (metricName: string) => void
}

const MetricInfoHeader = ({ metricAndGroupListItem, showMetricSelection, selectedMetrics, hideChart, handleMetricSelection }: Props) => {
  if (!metricAndGroupListItem || metricAndGroupListItem.showInTooltip) { return null }
  const isMetric = 'metricId' in metricAndGroupListItem

  const renderMetricCheckBox = () => (
    <Tooltip title={selectedMetrics.includes(metricAndGroupListItem.name) ? 'Unselect to remove metric from graph' : 'Select to add metric to graph'}>
      <S.CheckboxContainer onClick={e =>
        e.stopPropagation()
      }
      >
        <S.Checkbox
          label={
            <S.TitleUnitContainer>
              <span>{metricAndGroupListItem.name}</span>
              {metricAndGroupListItem.unit && <S.Unit>({metricAndGroupListItem.unit})</S.Unit>}
              {isMetric && <S.Subtitle><S.MetricColor bgColor={metricAndGroupListItem.color} data-testid={`metric-color-${metricAndGroupListItem.name}`} /></S.Subtitle>}
            </S.TitleUnitContainer>
          }
          isHtmlLabel
          checked={selectedMetrics.includes(metricAndGroupListItem.name)}
          onChange={() => {
            handleMetricSelection(metricAndGroupListItem.name)
          }}
          listId={metricAndGroupListItem.name}
          data-testid={`metric-checkbox-${metricAndGroupListItem.name}`}
        />
      </S.CheckboxContainer>
    </Tooltip>
  )

  return (
    <S.Header>
      <S.Title>
        {showMetricSelection && !hideChart
          ? renderMetricCheckBox()
          : (
            <S.TitleUnitContainer>
              <div><u>{metricAndGroupListItem.name}</u></div>
              {metricAndGroupListItem.unit && <S.Unit>({metricAndGroupListItem.unit})</S.Unit>}
              {!hideChart && <S.MetricColor bgColor={metricAndGroupListItem.color} />}
            </S.TitleUnitContainer>
          )
        }

        {!isMetric &&
          <S.GroupContainer>
            {_.sortBy(metricAndGroupListItem.metrics, 'sortPosition').map(({ metricId, name = '', color = '' }) => {
              return (
                <S.Group key={metricId}>
                  <S.Subtitle aria-label={`${metricAndGroupListItem.name} ${name}`}>
                    <S.MetricColor bgColor={color} />
                    {name}
                  </S.Subtitle>
                </S.Group>
              )
            })}
          </S.GroupContainer>
        }
      </S.Title>
    </S.Header>
  )
}

export default MetricInfoHeader

const S = {
  Header: styled.div`
    display: grid;
    padding: 3px 0px 10px 0;
    margin-right: 35px;
    grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
    grid-column-gap: 30px;
    background: ${color('paneBackground')};
    align-items: flex-start;
  `,
  Title: styled.h3`
    display: flex;
    text-transform: capitalize;
    margin: 0;
    width: 100%;
    flex-wrap: wrap;
    gap: 10px;
  `,
  TitleUnitContainer: styled.div`
    display: flex;
    text-transform: capitalize;
    flex-wrap: nowrap;
    align-items: center;
    overflow: hidden;
    flex-shrink: 1;
    min-width: 0;
    gap: 5px;
  `,
  Unit: styled.span`
    text-transform: none;
    flex-shrink: 0;
  `,
  Subtitle: styled.span`
    display: inline-block;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    max-width: 100%;
  `,
  MetricColor: styled.span.withConfig({
    shouldForwardProp: (prop) => !['bgColor'].includes(prop)
  }) <{ bgColor?: string }>`
    margin: 0px 10px 0 0;
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background-color: ${({ bgColor }) => bgColor || 'transparent'};
    display: inline-block;
  `,
  GroupContainer: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-left: 5px;
    flex-shrink: 1;
    overflow: hidden;
    min-width: 0;
  `,
  Group: styled.span`
    padding-right: 5px;
    min-width: 0;
  `,
  CheckboxContainer: styled.div`
    display: inline-block;
    overflow: hidden;
    flex-shrink: 1;
    min-width: 0;
  `,
  Checkbox: styled(Checkbox)`
    display: flex;
    margin: 0;
    label {
      flex-shrink: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-decoration: underline;
      min-width: 0;
    }
    input[type='checkbox'] {
      accent-color: ${color('secondaryButtonBorder')};
    }
  `
}
